import { useAuth } from '@/hooks'
import { useTransmit } from '@/hooks/useTransmit'
import {
    notificacoes_api,
    NotificationMessage,
    NotificationSilentMessage,
} from '@/service/api/endpoints/notificacoes'
import { StoredNotification } from '@/templates/Notificacoes/Central'
import { ExternalLinkIcon } from 'lucide-react'
import React, { createContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

interface NotificacoesContextProps {
    notifications: (NotificationMessage & { seen: boolean })[]
    silentNotifications: StoredNotification[]
    markAsSeen: (id: string | '*') => void
    isReady: boolean
}

type Props = {
    children: React.ReactNode
}

export const NotificacoesContext = createContext(
    {} as NotificacoesContextProps | undefined,
)

export const NotificacoesContextProvider: React.FC<Props> = ({ children }) => {
    const { decoded } = useAuth()
    const navigate = useNavigate()
    const [silentNotifications, setSilentNotifications] = useState<
        StoredNotification[]
    >([])
    const [notifications, setNotifications] = useState<
        (NotificationMessage & { seen: boolean })[]
    >([])
    const unsubscribeRef = React.useRef<() => void>()

    function updateSilent(value: NotificationSilentMessage) {
        return setSilentNotifications(current => {
            if (typeof value.body !== 'object' || value.body === null)
                return current

            const body = value.body as {
                value?: number | string
                ref?: string
                id?: string
            }

            const numericValue = Number(body.value)
            if (!Number.isFinite(numericValue)) return current

            const ref = body.ref ?? value.type
            const id = body.id ?? value.id
            if (!ref || !id) return current

            const transformed = {
                value: numericValue,
                module: value.module,
                type: value.type,
                ref,
                id,
            } satisfies StoredNotification

            const exists = current.find(
                n =>
                    transformed.module === n.module &&
                    transformed.type === n.type &&
                    transformed.ref === n.ref &&
                    transformed.id === n.id,
            )

            const shouldRemove = transformed.value >= 100
            if (shouldRemove) {
                return current.filter(
                    n =>
                        !(
                            transformed.module === n.module &&
                            transformed.type === n.type &&
                            transformed.ref === n.ref &&
                            transformed.id === n.id
                        ),
                )
            }
            if (exists) {
                return current.map(n =>
                    n.module === transformed.module &&
                    n.type === transformed.type &&
                    n.ref === transformed.ref &&
                    n.id === transformed.id
                        ? { ...n, value: transformed.value }
                        : n,
                )
            }

            return [...current, transformed]
        })
    }

    function addNotification(message: NotificationMessage) {
        return setNotifications(prev =>
            prev.some(n => n.id === message.id)
                ? prev
                : [...prev, { ...message, seen: false }].sort(
                      (a, b) =>
                          new Date(b.createdAt || '').getTime() -
                          new Date(a.createdAt || '').getTime(),
                  ),
        )
    }

    function markAsSeen(id: string | '*') {
        notificacoes_api.post('offset', {} as any)
        setNotifications(prev => {
            if (id === '*') {
                return prev.map(n => ({ ...n, seen: true }))
            }

            return prev.map(n => (n.id === id ? { ...n, seen: true } : n))
        })
    }

    const subscribe = useTransmit<NotificationMessage>({
        onMessage: payload => {
            if (payload.mode === 'silent') {
                updateSilent(payload)
                return
            }

            for (const mode of payload.mode) {
                switch (mode) {
                    case 'toast':
                        toast[payload.severity || 'info'](payload.message, {
                            id: payload.id,
                            description: payload.title,
                            actionButtonStyle: {
                                background: 'transparent',
                                color: 'inherit',
                                padding: 0,
                            },
                            action: payload.onClickUrl
                                ? {
                                      label: (
                                          <div
                                              title={payload.onClickUrl}
                                              className="flex items-center gap-1"
                                          >
                                              <ExternalLinkIcon className="size-4" />
                                          </div>
                                      ),
                                      type: 'div',
                                      onClick: () => {
                                          payload.onClickUrl!.startsWith('http')
                                              ? window.open(
                                                    payload.onClickUrl,
                                                    '_blank',
                                                )
                                              : navigate(payload.onClickUrl!)
                                      },
                                  }
                                : undefined,
                        })
                        break
                    case 'modal':
                        alert(`${payload.title}\n\n${payload.message}`) // temp
                        break
                    case 'badge':
                        addNotification(payload)
                        break
                    default:
                        throw new Error(
                            `Invalid notification mode: ${
                                mode satisfies never
                            }`,
                        )
                }
            }
        },
    })

    class SubscribeSingleton {
        private static instance: SubscribeSingleton
        public isSubscribedTo: string | null = null
        private isSubscribing = false
        private promise: any = null

        private constructor() {
            // Empty
        }

        public static getInstance(): SubscribeSingleton {
            if (!SubscribeSingleton.instance) {
                SubscribeSingleton.instance = new SubscribeSingleton()
            }
            return SubscribeSingleton.instance
        }

        public async subscribe(channel: string) {
            if (this.isSubscribing) return this.promise
            this.isSubscribing = true
            if (this.isSubscribedTo === channel) {
                return this.promise
            }

            return subscribe(channel)
                .then(unsubscribe => {
                    this.promise = unsubscribe
                })
                .finally(() => {
                    this.isSubscribing = false
                    this.isSubscribedTo = channel
                    notificacoes_api.missed.get()
                })
        }
    }

    const value: NotificacoesContextProps | undefined = !decoded.sub
        ? undefined
        : {
              notifications,
              silentNotifications,
              markAsSeen,
              isReady:
                  !!decoded.sub &&
                  !!SubscribeSingleton.getInstance().isSubscribedTo,
          }

    const channel = decoded.sub ? `notificacao/user/${decoded.sub}` : null

    React.useEffect(() => {
        const subscribeToNotifications = async () => {
            if (!channel) return

            unsubscribeRef.current =
                await SubscribeSingleton.getInstance().subscribe(channel)
        }
        subscribeToNotifications()

        return () => {
            unsubscribeRef.current?.()
        }
    }, [channel])

    return (
        <NotificacoesContext.Provider value={value}>
            {children}
        </NotificacoesContext.Provider>
    )
}
